import React, { useCallback } from 'react';
import { Icon, IconName } from '../../atoms/Icon';
import { Typography } from '../../atoms/Typography/Typography';
import { InlineProject } from '../../atoms/Activity/InlineProject';
import { CubeAnchorLink } from '../../atoms/CubeAnchorLink';
import Truncate from '../../utility/Truncate';
import { makeCSS, WithTheme } from '../../../utils/makeCSS';
import { isDateString } from '@cube3/common/utils/isDateString';
import Time from '../../utility/Time';
import { Free } from '../../templates/layout/Flex';
import { NotificationItemLayout } from '../../atoms/Activity/NotificationItemLayout';
import { Thumbnail } from '../../atoms/Thumbnail/Thumbnail';
import { Avatar } from '../../atoms/Avatar';
import { cx } from '@emotion/css';
import { SizeVariants, Theme } from '../../../theme/themes';
import { getContainerWidth } from '@cube3/common/utils/getContainerWidth';
import { BrandLogo } from '../../atoms/BrandLogo/BrandLogo';
import { isMobile } from 'react-device-detect';

type Chunk = {
  /** Display name */
  name: string;
  href?: string;
  onClick?: () => void;
  /** If it is defined, the text will be trimmed when the maximum width is reached, otherwise it will take up all the available width */
  maxWidth?: number;
  // bottomRowSection
  /** Required by `InlineProject` */
  icon?: IconName;
};
export type ActivityImageType = 'thumbnail' | 'avatar' | 'icon' | 'brand';
interface ActivityFeed {
  /** Default click event on the entire div */
  onDefaultClick?: (e) => void;
  fullWidth?: boolean;
  topRowSection: Array<string | Chunk>;
  bottomRowSection: Array<string | Chunk>;
  imageSection: {
    image: string;
    /** Used as auxiliary text for avatars */
    name?: string;
    type: ActivityImageType;
    /** Used to align different type of images */
    fixedWidth?: boolean;
  };
  indicatorSection?: React.ReactNode;
  dataSection?: React.ReactNode;
}

const useCSS = makeCSS(
  ({ theme, fullWidth }: WithTheme<Theme> & { fullWidth: boolean }) => {
    return {
      root: {
        display: 'flex',
        width: isMobile ? 'calc(100% - 12px)' : fullWidth ? '100%' : 358,
        maxWidth: 550,
        paddingLeft: theme.spacing[2],
        paddingRight: theme.spacing[2],
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: theme.borderRadius.lg
      },
      hoverStyle: {
        '&:hover': {
          background: theme.color.overlay.hovered
        }
      },
      pointer: { cursor: 'pointer' },
      cursorStyle: {
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center'
      },
      thumbnailContainer: {
        width: 64,
        height: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      },
      free: {
        display: 'inline-block',
        flexGrow: 1,
        flexShrink: 1
      }
    };
  }
);

export const ActivityItem = (props: ActivityFeed) => {
  const {
    fullWidth,
    onDefaultClick,
    imageSection,
    topRowSection,
    bottomRowSection,
    indicatorSection,
    dataSection
  } = props;
  const classes = useCSS({ fullWidth });

  return (
    <div
      className={cx(
        classes.root,
        classes.hoverStyle,
        onDefaultClick ? classes.pointer : ''
      )}
      onClick={onDefaultClick}
    >
      <NotificationItemLayout
        fullWidth={fullWidth}
        imageSection={<RenderImage {...imageSection} />}
        topRowSection={
          <>
            {topRowSection.filter(Boolean).map((chunk, idx) => (
              <RenderChunk key={chunk.toString() + idx} chunk={chunk} />
            ))}
          </>
        }
        bottomRowSection={
          <>
            {bottomRowSection.filter(Boolean).map((chunk, idx) => (
              <RenderChunk key={chunk.toString() + idx} chunk={chunk} />
            ))}
          </>
        }
        indicatorSection={indicatorSection}
        dataSection={dataSection}
      />
    </div>
  );
};

const RenderImage = ({ image, type, name = '', fixedWidth = true }) => {
  const classes = useCSS();
  const className = fixedWidth ? classes.thumbnailContainer : '';
  switch (type) {
    case 'brand':
      return (
        <div className={className}>
          {['export', 'broadcast', 'radio'].includes(image) ? (
            <Icon icon={image} size={SizeVariants['xl']} />
          ) : (
            <BrandLogo logo={image} size={SizeVariants['xl']} />
          )}
        </div>
      );
    case 'thumbnail':
      return <Thumbnail resource={{ display_image: image }} />;
    case 'icon':
      return (
        <div className={className}>
          <Icon icon={image} size={SizeVariants['xl']} />
        </div>
      );
    case 'avatar':
      return (
        <div className={className}>
          <Avatar imageUrl={image} userInitial={name} />
        </div>
      );
    default:
      return <Thumbnail resource={{ display_image: image }} />;
  }
};

const RenderChunk = React.memo(({ chunk }: { chunk: string | Chunk }) => {
  const classes = useCSS();

  const { setContainer, containerWidth } = getContainerWidth(4);

  const handleClick = useCallback(
    (e) => {
      if (typeof chunk === 'string') return;
      if (!chunk.onClick) return;
      // Used to prevent the default click event from being triggered
      e.stopPropagation();
      chunk.onClick();
    },
    [chunk]
  );

  if (typeof chunk === 'string') {
    if (isDateString(chunk)) {
      return (
        <Typography variant="md" colorVariant="03" weight="body">
          <Time
            date={chunk}
            //  noTime={true}
            relativeTime={true}
            toolTip={true}
            fullForm={false}
            shortStyle={true}
          />
        </Typography>
      );
    }
    return (
      <Typography variant="md" colorVariant="03" weight="body">
        {chunk}
      </Typography>
    );
  }
  const { name, href, maxWidth, icon, onClick } = chunk;
  if (chunk.icon) {
    return (
      <Free>
        <InlineProject
          icon={icon}
          projectName={name}
          projectHref={href}
          onClick={handleClick}
        />
      </Free>
    );
  }

  // This chunk will have a maxWidth
  if (maxWidth > 0) {
    return onClick ? (
      <Typography variant="md" colorVariant="01">
        <CubeAnchorLink
          href={href}
          injectedClassName={classes.cursorStyle}
          onLinkClick={handleClick}
          brightText={true}
        >
          <Truncate widthLimit={maxWidth} str={name} />
        </CubeAnchorLink>
      </Typography>
    ) : (
      <Typography variant="md" colorVariant="01">
        <Truncate widthLimit={maxWidth} str={name} />
      </Typography>
    );
  }
  // This chunk will take up all the available width
  return (
    <span
      className={classes.free}
      ref={setContainer}
      style={{ width: containerWidth }}
    >
      {onClick ? (
        <Typography variant="md" colorVariant="01">
          <CubeAnchorLink
            href={href}
            injectedClassName={classes.cursorStyle}
            onLinkClick={handleClick}
            brightText={true}
          >
            <Truncate str={name} widthLimit={containerWidth} />
          </CubeAnchorLink>
        </Typography>
      ) : (
        <Typography variant="md" colorVariant="01">
          <Truncate str={name} widthLimit={containerWidth} />
        </Typography>
      )}
    </span>
  );
});
